import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiBaseUrl, blogName } from '../config';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface BlogPost {
  id: number;
  date: string;
  title: { rendered: string };
  content: { rendered: string };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
    author?: Array<{
      name: string;
    }>;
  };
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/wp/v2/posts?slug=${slug}&_embed`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch blog post');
        }
        const data = await response.json();
        if (data.length === 0) {
          throw new Error('Post not found');
        }
        setPost(data[0]);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">{error || 'Post not found'}</div>
        <div className="text-center mt-4">
          <Link to={`/${blogName.toLowerCase()}`} className="text-blue-600 hover:text-blue-800">
            ← Back to {blogName}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <article className="max-w-4xl mx-auto">
        {post._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
          <img
            src={post._embedded['wp:featuredmedia'][0].source_url}
            alt={post.title.rendered}
            className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
          />
        )}
        
        <header className="mb-8">
          <h1 
            className="text-4xl font-bold mb-4"
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />
          <div className="flex items-center text-gray-600">
            {post._embedded?.author?.[0]?.name && (
              <span className="mr-4">By {post._embedded.author[0].name}</span>
            )}
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString()}
            </time>
          </div>
        </header>

        <div 
          className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content.rendered }}
        />

        <div className="mt-8 pt-8 border-t">
          <Link 
            to={`/${blogName.toLowerCase()}`}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to {blogName}
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPost; 